"use client";

import { c as _c } from "react/compiler-runtime";
import { EASE_OUT_QUART } from "@/lib/animation-constants";
import { motion } from "motion/react";
import { useEffect, useState, useSyncExternalStore } from "react";
import { GridBorderedSection, GridDivider } from "@/components/homepage/GridLayout";
import { HomepageFooter } from "@/components/homepage/HomepageFooter";
import { HomepageHeader } from "@/components/homepage/HomepageHeader";
import { useOrganizations } from "@/hooks/useOrganizations";
import { usePlanOptions } from "@/hooks/usePlanOptions";
import { useSubscription } from "@/hooks/useSubscription";
import { useListTiers } from "@/hooks/useTiers";
import Cookies from "js-cookie";
import { EnterpriseBanner } from "./EnterpriseBanner";
import { PricingCard } from "./PricingCard";
import { PricingComparison } from "./PricingComparison";
import { PricingFAQ } from "./PricingFAQ";
import { PricingHero } from "./PricingHero";
import { PRICING_TIERS } from "./pricing-data";
const DISPLAY_TIERS = PRICING_TIERS.filter(t => t.id !== "enterprise");
const ENTERPRISE_TIER = PRICING_TIERS.find(t => t.id === "enterprise");
export default function PricingPageClient() {
  const $ = _c(41);
  const [isYearly, setIsYearly] = useState(false);
  const {
    data: backendTiers
  } = useListTiers();
  const {
    selectedOrgId
  } = useOrganizations();
  const cookieOrgId = useSyncExternalStore(_temp2, _temp3, _temp4);
  const effectiveOrgId = selectedOrgId ?? cookieOrgId;
  const hasAuthCookie = !!Cookies.get("b3-auth");
  const {
    data: subscription
  } = useSubscription(hasAuthCookie && effectiveOrgId ? effectiveOrgId : undefined);
  const {
    data: planOptions
  } = usePlanOptions(hasAuthCookie && effectiveOrgId ? effectiveOrgId : undefined);
  const CACHE_KEY = hasAuthCookie && effectiveOrgId ? `b3os-tier-${effectiveOrgId}` : null;
  let t0;
  if ($[0] !== CACHE_KEY) {
    t0 = () => {
      if (!CACHE_KEY || typeof window === "undefined") {
        return null;
      }
      return window.localStorage.getItem(CACHE_KEY);
    };
    $[0] = CACHE_KEY;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const cachedTierId = useSyncExternalStore(_temp6, t0, _temp7);
  let t1;
  if ($[2] !== subscription?.subscription?.tier) {
    t1 = subscription?.subscription?.tier?.toLowerCase();
    $[2] = subscription?.subscription?.tier;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const apiTierId = t1;
  const currentTierId = apiTierId ?? cachedTierId ?? undefined;
  let t2;
  let t3;
  if ($[4] !== CACHE_KEY || $[5] !== apiTierId) {
    t2 = () => {
      if (apiTierId && CACHE_KEY) {
        localStorage.setItem(CACHE_KEY, apiTierId);
      }
    };
    t3 = [apiTierId, CACHE_KEY];
    $[4] = CACHE_KEY;
    $[5] = apiTierId;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <HomepageHeader />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <GridBorderedSection className="h-[72px]" />;
    t6 = <GridDivider variant={2} />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isYearly) {
    t7 = <GridBorderedSection><div className="mx-auto max-w-[1440px] px-[9px]"><div className="px-6"><PricingHero isYearly={isYearly} onToggle={setIsYearly} /></div></div></GridBorderedSection>;
    $[11] = isYearly;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <GridDivider variant={2} />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== backendTiers || $[15] !== currentTierId || $[16] !== isYearly || $[17] !== planOptions?.options) {
    t9 = DISPLAY_TIERS.map((tier, index) => <motion.div key={tier.id} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: index * 0.08,
      ease: EASE_OUT_QUART
    }}><PricingCard tier={tier} backendTier={backendTiers?.find(bt => bt.name.toLowerCase() === tier.id)} isYearly={isYearly} index={index} isCurrentTier={tier.id === currentTierId} currentTierId={currentTierId} planOption={planOptions?.options.find(o => o.tier === tier.id && o.interval === (isYearly ? "year" : "month"))} /></motion.div>);
    $[14] = backendTiers;
    $[15] = currentTierId;
    $[16] = isYearly;
    $[17] = planOptions?.options;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">{t9}</div>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== currentTierId) {
    t11 = ENTERPRISE_TIER && <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.4,
      ease: EASE_OUT_QUART
    }}><EnterpriseBanner tier={ENTERPRISE_TIER} isCurrentTier={currentTierId === "enterprise"} /></motion.div>;
    $[21] = currentTierId;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <GridBorderedSection><div className="mx-auto max-w-[1440px] px-[9px]"><div className="px-6"><section className="mx-auto max-w-[1200px] pb-16 pt-16">{t10}{t11}</section></div></div></GridBorderedSection>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <GridDivider variant={2} />;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <GridBorderedSection><div className="mx-auto max-w-[1440px] px-[9px]"><div className="h-[120px]" /></div></GridBorderedSection>;
    t15 = <GridDivider variant={2} />;
    $[27] = t14;
    $[28] = t15;
  } else {
    t14 = $[27];
    t15 = $[28];
  }
  let t16;
  if ($[29] !== backendTiers) {
    t16 = <GridBorderedSection><div className="mx-auto max-w-[1440px] px-[9px]"><PricingComparison backendTiers={backendTiers} /></div></GridBorderedSection>;
    $[29] = backendTiers;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <GridDivider variant={2} />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <GridBorderedSection><div className="mx-auto max-w-[1440px] px-[9px]"><div className="h-[120px]" /></div></GridBorderedSection>;
    t19 = <GridDivider variant={2} />;
    $[32] = t18;
    $[33] = t19;
  } else {
    t18 = $[32];
    t19 = $[33];
  }
  let t20;
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <GridBorderedSection><div className="mx-auto max-w-[1440px] px-[9px]"><div className="px-6"><PricingFAQ /></div></div></GridBorderedSection>;
    t21 = <GridDivider variant={2} />;
    $[34] = t20;
    $[35] = t21;
  } else {
    t20 = $[34];
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <GridBorderedSection><HomepageFooter /><GridDivider variant={2} /><GridBorderedSection className="h-[60px]" /></GridBorderedSection>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t12 || $[38] !== t16 || $[39] !== t7) {
    t23 = <div className="bg-ds-surface-primary relative min-h-screen">{t4}<div className="relative z-[2] px-4 2xl:px-0">{t5}{t6}{t7}{t8}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}</div></div>;
    $[37] = t12;
    $[38] = t16;
    $[39] = t7;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  return t23;
}
function _temp7() {
  return null;
}
function _temp6() {
  return _temp5;
}
function _temp5() {}
function _temp4() {
  return null;
}
function _temp3() {
  return Cookies.get("b3os-selected-orgId") ?? null;
}
function _temp2() {
  return _temp;
}
function _temp() {}